/**
 * The antd theme, derived entirely from the token layer.
 *
 * This file is the reason the rebrand is cheap. Ant Design's token system can
 * absorb almost everything the app used to express as `.ant-*` class overrides
 * plus `!important` — and anything expressed *here* re-skins for free the next
 * time the identity moves, whereas an override in a component does not.
 *
 * The working rule when you need to restyle an antd component:
 *
 *   1. Can a global token do it? Put it in `token` below.
 *   2. Can a component token do it? Put it in `components` below.
 *   3. Does the component expose a prop or a `classNames`/`styles` slot? Use it.
 *   4. Only then consider owning the component outright in `components/ui`.
 *
 * Reaching into antd's internal DOM (`& .ant-tabs-tab`) is not on that list.
 * Those class names are private, they change on minor upgrades, and beating
 * antd's runtime-injected CSS on specificity is what produced the 161
 * `!important`s this replaces.
 */

import type { ThemeConfig } from 'antd';

import { palette } from './palette';
import { colors } from './semantic';
import { duration, fontFamily, fontSize, fontWeight, lineHeight, radius, shadows, size, space, zIndex } from './tokens';

export const antdTheme: ThemeConfig = {
  token: {
    /* --- Brand ------------------------------------------------------------ */
    colorPrimary: colors.brand.primary,
    /*
     * What antd puts on a solid primary fill. Its default is white, which was
     * correct while `brand.primary` was deep teal and became a 1.77:1 failure
     * the moment primary and accent were swapped — turquoise is a fill, and
     * white on it is unreadable. Binding it to the token means the pair moves
     * together next time, instead of one half of it silently staying white.
     */
    colorTextLightSolid: colors.brand.onPrimary,
    colorSuccess: colors.feedback.success.solid,
    colorWarning: colors.feedback.warning.solid,
    colorError: colors.feedback.error.solid,
    colorInfo: colors.feedback.info.solid,

    /* --- Text ------------------------------------------------------------- */
    colorTextBase: colors.text.primary,
    colorText: colors.text.primary,
    colorTextSecondary: colors.text.secondary,
    colorTextTertiary: colors.text.tertiary,
    colorTextQuaternary: colors.text.placeholder,
    colorTextDisabled: colors.text.disabled,
    colorTextPlaceholder: colors.text.placeholder,
    colorTextHeading: colors.text.primary,
    colorTextDescription: colors.text.tertiary,
    colorLink: colors.text.link,
    colorLinkHover: colors.text.linkHover,
    colorLinkActive: colors.text.linkHover,

    /* --- Surfaces --------------------------------------------------------- */
    colorBgBase: colors.surface.base,
    colorBgContainer: colors.surface.base,
    colorBgElevated: colors.surface.base,
    colorBgLayout: colors.surface.page,
    colorBgSpotlight: colors.surface.inverse,
    colorBgMask: colors.surface.scrim,
    colorFillAlter: colors.surface.sunken,
    colorFillSecondary: colors.surface.muted,
    colorFillTertiary: colors.surface.sunken,
    colorFillQuaternary: colors.surface.sunken,

    /* --- Borders ---------------------------------------------------------- */
    colorBorder: colors.border.default,
    colorBorderSecondary: colors.border.subtle,
    lineWidth: 1,

    /* --- Typography ------------------------------------------------------- */
    fontFamily: fontFamily.text,
    fontFamilyCode: fontFamily.mono,
    fontSize: fontSize.md,
    fontSizeSM: fontSize.sm,
    fontSizeLG: fontSize.lg,
    fontSizeXL: fontSize.xl,
    fontSizeHeading1: fontSize['3xl'],
    fontSizeHeading2: fontSize['2xl'],
    fontSizeHeading3: fontSize['2xl'],
    fontSizeHeading4: fontSize.xl,
    fontSizeHeading5: fontSize.lg,
    lineHeight: lineHeight.normal,
    lineHeightHeading1: lineHeight.tight,
    lineHeightHeading2: lineHeight.snug,
    lineHeightHeading3: lineHeight.snug,
    lineHeightHeading4: lineHeight.snug,
    lineHeightHeading5: lineHeight.snug,
    /**
     * antd uses this for every heading and for `<strong>`. The identity calls
     * for light titles, so it drops from antd's default 600 to regular; the
     * `Title` primitive in `components/ui` goes further and sets 300 on the
     * large sizes, where a light cut reads best.
     */
    fontWeightStrong: fontWeight.regular,

    /* --- Shape ------------------------------------------------------------ */
    borderRadius: radius.md,
    borderRadiusXS: radius.sm,
    borderRadiusSM: radius.sm,
    borderRadiusLG: radius.lg,

    /* --- Elevation -------------------------------------------------------- */
    /** Flat identity: shadows are a whisper, and most surfaces have none. */
    boxShadow: shadows.sm,
    boxShadowSecondary: shadows.md,
    boxShadowTertiary: shadows.xs,

    /* --- Motion ----------------------------------------------------------- */
    motionDurationFast: `${duration.fast}ms`,
    motionDurationMid: `${duration.base}ms`,
    motionDurationSlow: `${duration.slow}ms`,

    /* --- Controls --------------------------------------------------------- */
    controlHeight: size.touchTarget,
    zIndexPopupBase: zIndex.overlay,

    /** antd's "wireframe" mode adds borders everywhere; the flat look wants fills. */
    wireframe: false,
  },

  components: {
    /**
     * Buttons are flat. The old `0px 5px rgba(100,100,100,0.2)` hard offset
     * shadow is gone in favour of colour contrast alone.
     */
    Button: {
      /* `--radius-pill`: the design system makes buttons capsules by default. */
      borderRadius: radius.pill,
      borderRadiusSM: radius.pill,
      borderRadiusLG: radius.pill,
      fontWeight: fontWeight.medium,
      defaultShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
      contentFontSize: fontSize.md,
      contentFontSizeLG: fontSize.lg,
      contentFontSizeSM: fontSize.sm,
      paddingInline: space.xl,
      paddingBlock: space.sm,
      defaultBg: colors.surface.sunken,
      defaultColor: colors.text.primary,
      defaultBorderColor: 'transparent',
      defaultHoverBg: palette.sand[200],
      defaultHoverColor: colors.text.primary,
      defaultHoverBorderColor: 'transparent',
      defaultActiveBg: palette.sand[200],
      defaultActiveColor: colors.text.primary,
      defaultActiveBorderColor: 'transparent',
      defaultBgDisabled: colors.surface.disabled,
      borderColorDisabled: 'transparent',
      textHoverBg: colors.surface.sunken,
      textTextColor: colors.text.primary,
      textTextHoverColor: colors.text.primary,
      textTextActiveColor: colors.text.primary,
      solidTextColor: colors.brand.onPrimary,
      linkHoverBg: 'transparent',
    },

    /** Cards carry the page's separation. Borderless, flat, generous padding. */
    Card: {
      headerBg: 'transparent',
      headerFontSize: fontSize.lg,
      headerPadding: space.lg,
      bodyPadding: space.lg,
      bodyPaddingSM: space.md,
      headerPaddingSM: space.md,
      actionsBg: colors.surface.sunken,
      extraColor: colors.text.secondary,
    },

    /**
     * Replaces the hand-rolled `.ant-tabs-tab` pill styling that
     * `CalendarTabs` used to carry.
     */
    Tabs: {
      itemColor: colors.text.secondary,
      itemHoverColor: colors.text.primary,
      itemSelectedColor: colors.text.primary,
      itemActiveColor: colors.text.primary,
      inkBarColor: colors.brand.accent,
      cardBg: colors.surface.sunken,
      cardPadding: `${space.sm}px ${space.lg}px`,
      titleFontSize: fontSize.md,
      titleFontSizeSM: fontSize.sm,
      titleFontSizeLG: fontSize.lg,
      horizontalItemGutter: space.lg,
      horizontalItemPadding: `${space.sm}px ${space.md}px`,
    },

    /** Inputs sit in a recessed well rather than behind a hard border. */
    Input: {
      paddingBlock: space.sm,
      paddingInline: space.md,
      inputFontSize: fontSize.md,
      addonBg: colors.surface.sunken,
      hoverBg: colors.surface.base,
      activeBg: colors.surface.base,
      hoverBorderColor: colors.border.strong,
      activeBorderColor: colors.border.focus,
      /** Flat: focus is a border change, not a glow. */
      activeShadow: 'none',
      errorActiveShadow: 'none',
      warningActiveShadow: 'none',
    },

    InputNumber: {
      paddingBlock: space.sm,
      inputFontSize: fontSize.md,
      hoverBorderColor: colors.border.strong,
      activeBorderColor: colors.border.focus,
      activeShadow: 'none',
    },

    Select: {
      optionSelectedBg: colors.brand.accentSubtle,
      optionSelectedColor: colors.text.primary,
      optionSelectedFontWeight: fontWeight.medium,
      optionActiveBg: colors.surface.sunken,
      optionFontSize: fontSize.md,
      optionPadding: `${space.sm}px ${space.md}px`,
      selectorBg: colors.surface.base,
      hoverBorderColor: colors.border.strong,
      activeBorderColor: colors.border.focus,
      activeOutlineColor: 'transparent',
      multipleItemBg: colors.brand.accentSubtle,
    },

    Modal: {
      contentBg: colors.surface.base,
      headerBg: 'transparent',
      footerBg: 'transparent',
      titleColor: colors.text.primary,
      titleFontSize: fontSize.xl,
      titleLineHeight: lineHeight.snug,
    },

    Drawer: {
      footerPaddingBlock: space.md,
      footerPaddingInline: space.lg,
    },

    /**
     * Collapse panels read as quiet sections, not as boxes — this is what lets
     * the FAQ and questionnaire screens drop their border overrides.
     */
    Collapse: {
      headerBg: 'transparent',
      contentBg: 'transparent',
      borderlessContentBg: 'transparent',
      headerPadding: `${space.md}px ${space.none}px`,
      contentPadding: `${space.none}px ${space.none}px ${space.md}px`,
      borderlessContentPadding: `${space.none}px ${space.none}px ${space.md}px`,
    },

    Segmented: {
      trackBg: colors.surface.sunken,
      trackPadding: space.xs,
      itemColor: colors.text.secondary,
      itemHoverColor: colors.text.primary,
      itemHoverBg: 'transparent',
      itemSelectedBg: colors.surface.base,
      itemSelectedColor: colors.text.primary,
      // Left at the seed defaults, the track uses `borderRadius` (16) and the
      // selected thumb uses `borderRadiusSM` (8) — two different curves that
      // don't nest, so the thumb looks pointy inside the pill-shaped track.
      // Pill for both makes the two always the same shape.
      borderRadius: radius.pill,
      borderRadiusSM: radius.pill,
      borderRadiusLG: radius.pill,
    },

    /** Replaces the `.ant-dropdown-menu-item` padding override in `PageTopBar`. */
    Dropdown: {
      paddingBlock: space.md,
      zIndexPopup: zIndex.overlay,
    },

    Menu: {
      itemBg: 'transparent',
      itemColor: colors.text.primary,
      itemHoverBg: colors.surface.sunken,
      itemHoverColor: colors.text.primary,
      itemSelectedBg: colors.brand.accentSubtle,
      itemSelectedColor: colors.text.primary,
      itemBorderRadius: radius.md,
      itemPaddingInline: space.lg,
      iconMarginInlineEnd: space.md,
      itemHeight: size.touchTarget,
    },

    Tag: {
      /* `--radius-pill`, same rule as Button. */
      borderRadiusSM: radius.pill,
      defaultBg: colors.surface.sunken,
      defaultColor: colors.text.secondary,
      solidTextColor: colors.brand.onPrimary,
    },

    Alert: {
      defaultPadding: `${space.md}px ${space.lg}px`,
      withDescriptionPadding: `${space.lg}px`,
      colorInfoBg: colors.feedback.info.bg,
      colorInfoBorder: colors.feedback.info.border,
    },

    Notification: {
      zIndexPopup: zIndex.notification,
      colorSuccessBg: colors.feedback.success.bg,
      colorErrorBg: colors.feedback.error.bg,
      colorInfoBg: colors.feedback.info.bg,
      colorWarningBg: colors.feedback.warning.bg,
    },

    Typography: {
      titleMarginTop: space.none,
      titleMarginBottom: space.md,
    },

    Form: {
      labelColor: colors.text.secondary,
      labelFontSize: fontSize.sm,
      labelRequiredMarkColor: colors.feedback.error.solid,
      itemMarginBottom: space.lg,
    },

    List: {
      itemPadding: `${space.md}px ${space.none}px`,
      itemPaddingSM: `${space.sm}px ${space.none}px`,
      itemPaddingLG: `${space.lg}px ${space.none}px`,
      headerBg: 'transparent',
      footerBg: 'transparent',
      descriptionFontSize: fontSize.sm,
    },

    Divider: {
      textPaddingInline: space.lg,
    },

    Progress: {
      defaultColor: colors.brand.accent,
      remainingColor: colors.surface.sunken,
      circleTextColor: colors.text.primary,
      lineBorderRadius: radius.pill,
    },

    Radio: {
      buttonBg: colors.surface.base,
      buttonCheckedBg: colors.brand.accentSubtle,
      buttonColor: colors.text.secondary,
      buttonSolidCheckedBg: colors.brand.primary,
      buttonSolidCheckedColor: colors.brand.onPrimary,
      buttonSolidCheckedHoverBg: colors.brand.primaryHover,
      buttonSolidCheckedActiveBg: colors.brand.primaryActive,
      dotColorDisabled: colors.text.disabled,
    },

    Switch: {
      handleBg: colors.surface.base,
      handleShadow: shadows.xs,
    },

    Slider: {
      railBg: colors.surface.sunken,
      railHoverBg: colors.border.subtle,
      trackBg: colors.brand.accent,
      trackHoverBg: colors.brand.accentHover,
      handleColor: colors.brand.primary,
      handleActiveColor: colors.brand.primary,
      handleActiveOutlineColor: 'transparent',
      dotBorderColor: colors.border.default,
      dotActiveBorderColor: colors.brand.accent,
    },

    Skeleton: {
      color: colors.surface.sunken,
      colorGradientEnd: colors.surface.muted,
      blockRadius: radius.md,
    },

    Avatar: {
      groupBorderColor: colors.surface.base,
    },

    Badge: {
      textFontWeight: fontWeight.medium,
    },

    Calendar: {
      fullBg: 'transparent',
      fullPanelBg: 'transparent',
      itemActiveBg: colors.brand.accentSubtle,
    },

    DatePicker: {
      cellHoverBg: colors.surface.sunken,
      cellActiveWithRangeBg: colors.brand.accentSubtle,
      cellHoverWithRangeBg: colors.brand.accentSubtle,
      activeBorderColor: colors.border.focus,
      hoverBorderColor: colors.border.strong,
      activeShadow: 'none',
      // The dropdown panel's width is `cellWidth * 7 + horizontalPadding * 2`
      // (the padding is ~18px and isn't itself exposed as an overridable
      // token), computed once from these into a fixed pixel value at theme
      // build time — not something a runtime CSS override can reach, since no
      // rule actually sums to that number at paint time. Left at the seed
      // default (a 49.5px cell inherited from the app's enlarged,
      // touch-friendly `controlHeightSM`), the panel comes to 382.5px, wider
      // than a 375px phone: the popup renders partly off the left edge, with
      // no way to scroll to the cut-off column. Sized down for this panel
      // specifically — `40 * 7 + 36 = 316px` — without touching
      // `controlHeightSM` itself, which other, non-cramped controls still use.
      cellWidth: 40,
    },

    Steps: {
      titleLineHeight: lineHeight.snug,
    },

    Spin: {
      dotSize: space['2xl'],
    },
  },
};

export default antdTheme;
